import { Controller } from "stimulus";

// Library question editor: add/delete options and per-option connect-follow-up picker.
export default class extends Controller {
  static targets = [
    "optionsList",
    "optionRow",
    "addOptionButton",
    "optionTemplate",
    "connectPicker",
    "existingPicker",
    "createNewPanel",
    "maxOptionsHint"
  ];
  // Stimulus 2.x expects constructors (Number), not Stimulus 3 `{ type, default }` objects.
  // Defaults come from data-*-value attributes on the controller element.
  static values = {
    minOptions: Number,
    maxOptions: Number
  };

  connect() {
    this.refreshOptionControls();
  }

  addOption(event) {
    event.preventDefault();
    if (this.activeOptionCount() >= this.maxOptionsValue) return;
    if (!this.hasOptionTemplateTarget || !this.hasOptionsListTarget) return;

    const index = Date.now();
    const html = this.optionTemplateTarget.innerHTML.replace(/NEW_OPTION/g, String(index));
    this.optionsListTarget.insertAdjacentHTML("beforeend", html);
    this.refreshOptionControls();

    const rows = this.optionRowTargets;
    const last = rows[rows.length - 1];
    const input = last && last.querySelector("input[type='text']");
    if (input) input.focus();
  }

  deleteOption(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const row = button.closest("[data-qualification-question-form-target='optionRow']");
    if (!row) return;

    if (this.activeOptionCount() <= this.minOptionsValue) {
      window.alert(`Questions need at least ${this.minOptionsValue} options.`);
      return;
    }

    const deleteUrl = button.dataset.deleteUrl;
    if (deleteUrl) {
      const blockers = JSON.parse(button.dataset.blockers || "[]");
      if (blockers.length) {
        window.alert(blockers.join("\n\n"));
        return;
      }

      const warnings = JSON.parse(button.dataset.warnings || "[]");
      const message = [
        "Delete this option?",
        "It will be hidden from new registration forms. Past answers stay unchanged.",
        ...warnings
      ].join("\n\n");
      if (!window.confirm(message)) return;

      this.submitMethod(deleteUrl, "delete", button.dataset.csrfToken);
      return;
    }

    row.remove();
    this.refreshOptionControls();
  }

  // Open/close the connect panel on the clicked option row.
  // Prefer row-scoped lookup over matching data-option-id across all targets —
  // ID mismatch previously made the click a silent no-op.
  toggleConnectPicker(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const row = button.closest("[data-qualification-question-form-target='optionRow']");
    if (!row) return;

    const panel = row.querySelector("[data-qualification-question-form-target='connectPicker']");
    if (!panel) return;

    const willOpen = panel.classList.contains("d-none");
    this.closeAllConnectPickers();
    if (!willOpen) return;

    panel.classList.remove("d-none");
    this.setConnectButtonOpen(button, true);
    this.showExistingPickerIn(row);
    // Keep focus visible for keyboard / narrow layouts.
    panel.setAttribute("tabindex", "-1");
    if (typeof panel.focus === "function") panel.focus({ preventScroll: true });
  }

  hideConnectPicker(event) {
    event.preventDefault();
    const row = event.currentTarget.closest("[data-qualification-question-form-target='optionRow']");
    if (!row) return;
    const panel = row.querySelector("[data-qualification-question-form-target='connectPicker']");
    if (panel) panel.classList.add("d-none");
    const button = row.querySelector(".qq-connect-btn");
    if (button) this.setConnectButtonOpen(button, false);
  }

  showCreateNew(event) {
    event.preventDefault();
    const row = event.currentTarget.closest("[data-qualification-question-form-target='optionRow']");
    if (!row) return;
    const existing = row.querySelector("[data-qualification-question-form-target='existingPicker']");
    const createNew = row.querySelector("[data-qualification-question-form-target='createNewPanel']");
    if (existing) existing.classList.add("d-none");
    if (createNew) createNew.classList.remove("d-none");
  }

  showExistingPicker(event) {
    event.preventDefault();
    const row = event.currentTarget.closest("[data-qualification-question-form-target='optionRow']");
    if (row) this.showExistingPickerIn(row);
  }

  showExistingPickerIn(row) {
    const existing = row.querySelector("[data-qualification-question-form-target='existingPicker']");
    const createNew = row.querySelector("[data-qualification-question-form-target='createNewPanel']");
    if (existing) existing.classList.remove("d-none");
    if (createNew) createNew.classList.add("d-none");
  }

  closeAllConnectPickers() {
    this.element.querySelectorAll("[data-qualification-question-form-target='connectPicker']").forEach((panel) => {
      panel.classList.add("d-none");
    });
    this.element.querySelectorAll(".qq-connect-btn").forEach((button) => {
      this.setConnectButtonOpen(button, false);
    });
  }

  setConnectButtonOpen(button, open) {
    button.setAttribute("aria-expanded", open ? "true" : "false");
    button.classList.toggle("active", open);
    button.classList.toggle("btn-primary", open);
    button.classList.toggle("btn-outline-primary", !open);
  }

  refreshOptionControls() {
    const count = this.activeOptionCount();
    const atMax = count >= this.maxOptionsValue;

    if (this.hasAddOptionButtonTarget) {
      this.addOptionButtonTarget.disabled = atMax;
      this.addOptionButtonTarget.classList.toggle("d-none", atMax);
    }
    if (this.hasMaxOptionsHintTarget) {
      this.maxOptionsHintTarget.classList.toggle("d-none", !atMax);
    }

    this.optionRowTargets.forEach((row) => {
      const deleteBtn = row.querySelector("[data-action*='deleteOption']");
      if (deleteBtn && !deleteBtn.dataset.deleteUrl) {
        deleteBtn.disabled = count <= this.minOptionsValue;
      }
    });
  }

  activeOptionCount() {
    return this.optionRowTargets.length;
  }

  submitMethod(url, method, csrfToken) {
    const form = document.createElement("form");
    form.method = "post";
    form.action = url;
    form.style.display = "none";

    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = method;
    form.appendChild(methodInput);

    const token = document.createElement("input");
    token.type = "hidden";
    token.name = "authenticity_token";
    token.value = csrfToken || this.csrfToken();
    form.appendChild(token);

    document.body.appendChild(form);
    form.submit();
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']");
    return meta ? meta.content : "";
  }
}
